import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, Views, View, momentLocalizer } from 'react-big-calendar';
import { format, parse, isSameDay, isAfter, setHours, setMinutes, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import moment from 'moment';
import 'moment/locale/pt-br';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { FaTimes, FaCalendarAlt } from 'react-icons/fa';

import { CronogramaViewModel } from '../../../viewmodel/CronogramaViewModel';
import { DataBancaViewModel } from '../../../viewmodel/banca/DataBancaViewModel';
import { ProcessoCronograma } from '../../Cronograma/CronogramaGeral';
import Banca, { TipoBancaNome } from '../../../model/banca/Banca';
import { StatusMembroNome } from '../../../model/banca/MembroBanca';
import BackendErrorMessage from '../../../components/BackendErrorMessage';
import { BancaTagUtils } from '../../../utils/BancaTagUtils';
import { useProcessoContext } from '../../../Routes/context/ProcessoUsuarioContext';

moment.locale('pt-br');
const localizer = momentLocalizer(moment);

interface CronogramaModalProps {
  banca: Banca;
  processo?: any;
  onClose: () => void;
  onSaveSuccess: () => void;
}

const CronogramaModal: React.FC<CronogramaModalProps> = ({
  banca,
  processo: processoProps,
  onClose,
  onSaveSuccess
}) => {
  const { processo: processoContext } = useProcessoContext();
  const processo = processoProps || processoContext;

  const getTipoBancaIcon = (tipoBanca: string) => {
    switch (tipoBanca) {
      case TipoBancaNome.FACIAL:
        return '📝';
      case TipoBancaNome.RECURSAL:
        return '⚖️';
      case TipoBancaNome.VIRTUAL:
        return '💻';
      default:
        return '🏢';
    }
  };

  const getPresidenteNome = () => {
    // Primeiro tenta encontrar um docente titular confirmado/definido
    const docente = banca.membros.find(membro => 
      membro.papelMembro.nome === 'Titular' && 
      membro.pessoa.vinculo.nome === 'Docente' && 
      (membro.statusMembro === StatusMembroNome.CONFIRMADO || 
        membro.statusMembro === StatusMembroNome.DEFINIDO || 
        membro.statusMembro === StatusMembroNome.CONVOCACAO_CONFIRMADA || 
        membro.statusMembro === StatusMembroNome.CONVOCADO)
    );
    
    if (docente) return docente.pessoa.nome;
    
    return 'Presidente não definido';
  };
  const [cronogramaData, setCronogramaData] = useState<ProcessoCronograma[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [startTimeInput, setStartTimeInput] = useState<string>('');
  const [endTimeInput, setEndTimeInput] = useState<string>('');
  const [timeErrors, setTimeErrors] = useState<{start?: string, end?: string}>({});
  const [view, setView] = useState<View>(Views.MONTH);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const cronograma = await CronogramaViewModel.getCronogramaGeral();
        setCronogramaData(cronograma);

        const { data: dateStr, horaInicial, horaFinal, isValid } = await DataBancaViewModel.getRawDataBanca(processo?.id!, banca.id);
        
        if (dateStr && horaInicial && horaFinal && isValid) {
          const parsedDate = parse(dateStr, 'yyyy-MM-dd', new Date());
          setSelectedDate(parsedDate);
          setCalendarDate(parsedDate);
          
          const startDateTime = parse(`${dateStr} ${horaInicial}`, 'yyyy-MM-dd HH:mm:ss', new Date());
          setStartTime(startDateTime);
          setStartTimeInput(format(startDateTime, 'HH:mm'));
          
          const endDateTime = parse(`${dateStr} ${horaFinal}`, 'yyyy-MM-dd HH:mm:ss', new Date());
          setEndTime(endDateTime);
          setEndTimeInput(format(endDateTime, 'HH:mm'));
        }
      } catch (error) {
        console.error("Erro ao buscar dados", error);
      }
    };

    fetchData();
  }, [processo?.id, banca.id]);

  const eventos = useMemo(() => [
    ...cronogramaData.flatMap(pc =>
      pc.cronograma.atividades.map(atividade => {
        const atividadeDate = parse(`${atividade.data} ${atividade.hora}`, 'yyyy-MM-dd HH:mm', new Date());
        const endDate = new Date(atividadeDate.getTime() + 60 * 60 * 1000);
        
        const isBanca = BancaTagUtils.isBancaActivity(atividade.descricao);
        const isCurrentProcess = pc.processo.nome === processo?.nome || pc.processo.id === processo?.id;

        const titlePrefix = isBanca && isCurrentProcess ? '● [MEU] ' : 
                           isBanca ? '● ' : '';
        
        return {
          id: atividade.id,
          title: `${titlePrefix}${BancaTagUtils.removeBancaTag(atividade.descricao)}`,
          start: atividadeDate,
          end: endDate,
          resource: {
            ...atividade,
            processo: pc.processo,
            isBanca,
            isCurrentProcess
          }
        };
      })
    ),
    // Evento visual da banca sendo definida
    ...(selectedDate && startTime && endTime ? [{
      id: 'preview-banca',
      title: `${getTipoBancaIcon(banca.tipoBanca.nome)} [NOVA] ${banca.tipoBanca.nome.replace('Banca de Heteroidentificação ', '')} - ${getPresidenteNome()}`,
      start: setHours(setMinutes(selectedDate, startTime.getMinutes()), startTime.getHours()),
      end: setHours(setMinutes(selectedDate, endTime.getMinutes()), endTime.getHours()),
      resource: {
        isPreview: true,
        isBanca: true,
        isCurrentProcess: true
      }
    }] : [])
  ], [cronogramaData, selectedDate, startTime, endTime, processo?.nome, processo?.id, banca.tipoBanca.nome]);

  const eventStyleGetter = (event: any) => {
    const { isBanca, isCurrentProcess, isPreview } = event.resource || {};
    
    // Evento de preview da nova banca
    if (isPreview) {
      return {
        style: {
          backgroundColor: '#e3f2fd',
          border: '3px dashed #2196f3',
          color: '#1565c0',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: 'bold',
          opacity: 0.9
        }
      };
    }
    
    if (isBanca) {
      if (isCurrentProcess) {
        return {
          style: {
            backgroundColor: '#d4edda',
            border: '2px solid #28a745',
            color: '#155724',
            borderRadius: '3px',
            fontSize: '11px',
            fontWeight: 'bold'
          }
        };
      } else {
        return {
          style: {
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            color: '#856404',
            borderRadius: '3px',
            fontSize: '11px'
          }
        };
      }
    }

    return {
      style: {
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        color: '#6c757d',
        borderRadius: '3px',
        fontSize: '10px',
        opacity: 0.7
      }
    };
  };

  const getAtividadesForDate = (date: Date | null) => {
    if (!date) return [];
    
    return cronogramaData.flatMap(pc => 
      pc.cronograma.atividades
        .filter(atividade => {
          const atividadeDate = parse(atividade.data, 'yyyy-MM-dd', new Date());
          return isSameDay(atividadeDate, date);
        })
        .map(atividade => {
          const isBanca = BancaTagUtils.isBancaActivity(atividade.descricao);
          const isCurrentProcess = pc.processo.nome === processo?.nome || pc.processo.id === processo?.id;
          
          return {
            ...atividade,
            processo: pc.processo,
            isBanca,
            isCurrentProcess
          };
        })
    );
  };

  const atividades = getAtividadesForDate(selectedDate);

  const validateTimeInput = (timeStr: string): Date | null => {
    if (!timeStr.trim()) return null;
    
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
    if (!timeRegex.test(timeStr)) return null;
    
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return isValid(date) ? date : null;
  };

  const handleTimeChange = (value: string, type: 'start' | 'end') => {
    const cleanValue = value.replace(/[^0-9:]/g, '');
    
    if (type === 'start') {
      setStartTimeInput(cleanValue);
      const parsedTime = validateTimeInput(cleanValue);
      setStartTime(parsedTime);
      setTimeErrors(prev => ({ ...prev, start: parsedTime ? undefined : cleanValue ? 'Formato inválido (HH:MM)' : undefined }));
    } else {
      setEndTimeInput(cleanValue);
      const parsedTime = validateTimeInput(cleanValue);
      setEndTime(parsedTime);
      setTimeErrors(prev => ({ ...prev, end: parsedTime ? undefined : cleanValue ? 'Formato inválido (HH:MM)' : undefined }));
    }
  };

  const handleSave = async () => {
    if (!selectedDate || !startTime || !endTime) {
      toast.error('Selecione data, hora de início e fim');
      return;
    }
    
    if (timeErrors.start || timeErrors.end) {
      toast.error('Corrija os horários inválidos');
      return;
    }

    const combinedStart = setHours(setMinutes(selectedDate, startTime.getMinutes()), startTime.getHours());
    const combinedEnd = setHours(setMinutes(selectedDate, endTime.getMinutes()), endTime.getHours());

    if (!isAfter(combinedStart, new Date()) || !isAfter(combinedEnd, combinedStart)) {
      toast.error('Data/hora inválida');
      return;
    }

    setLoading(true);
    try {
      const success = await DataBancaViewModel.postDataBanca(
        processo?.id!,
        banca.id,
        combinedStart.toISOString().split('T')[0],
        combinedStart.toTimeString().split(' ')[0],
        combinedEnd.toTimeString().split(' ')[0]
      );

      if (success) {
        toast.success('Data da banca definida com sucesso');
        onSaveSuccess();
        onClose();
      }
    } catch (error: any) {
      toast.error(BackendErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999
      }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        width: '95vw',
        height: '95vh',
        overflow: 'hidden',
        boxShadow: '0 6px 10px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '15px 20px',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f8f9fa'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              Cronograma de Referência
              <FaCalendarAlt style={{ color: '#6c757d' }} />
            </h4>
            <div style={{ fontSize: '12px', color: '#6c757d', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{getTipoBancaIcon(banca.tipoBanca.nome)} {banca.tipoBanca.nome.replace('Banca de Heteroidentificação ', '')}</span>
              <span>•</span>
              <span>{processo?.nome}</span>
              <span>•</span>
              <span>{getPresidenteNome()}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#6c757d'
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Calendar */}
          <div style={{ flex: 1, padding: '20px', minHeight: 0 }}>
            <div style={{ height: '100%', minHeight: '600px' }}>
              <Calendar
                localizer={localizer}
                events={eventos}
                startAccessor="start"
                endAccessor="end"
                style={{ height: '100%' }}
                view={view}
                onView={setView}
                date={calendarDate}
                onNavigate={setCalendarDate}
                eventPropGetter={eventStyleGetter}
                onSelectSlot={({ start }) => setSelectedDate(start)}
                onSelectEvent={(event) => {
                  // Não permitir interação com o evento de preview
                  if ((event.resource as any)?.isPreview) return;
                  
                  if (event.resource?.isBanca && event.resource?.isCurrentProcess) {
                    const eventDate = new Date(event.start);
                    const eventEndDate = new Date(event.end);
                    
                    setSelectedDate(eventDate);
                    setStartTime(eventDate);
                    setEndTime(eventEndDate);
                    setStartTimeInput(format(eventDate, 'HH:mm'));
                    setEndTimeInput(format(eventEndDate, 'HH:mm'));
                    
                    toast.info(`Horário assumido: ${format(eventDate, 'HH:mm')} às ${format(eventEndDate, 'HH:mm')}`);
                  }
                }}
                selectable
                messages={{
                  today: 'Hoje',
                  previous: 'Anterior',
                  next: 'Próximo',
                  month: 'Mês',
                  week: 'Semana',
                  day: 'Dia'
                }}
              />
            </div>
          </div>

          {/* Sidebar */}
          <div style={{
            width: '350px',
            borderLeft: '1px solid #eee',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#f8f9fa'
          }}>
            {/* Definir Data e Horário */}
            <div style={{ 
              padding: '20px', 
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '6px',
              margin: '10px'
            }}>
              <h6 style={{ 
                margin: '0 0 15px 0', 
                color: '#495057', 
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                Definir Data e Horário
              </h6>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '5px', display: 'block', color: '#495057' }}>Data:</label>
                <DatePicker
                  selected={selectedDate}
                  onChange={setSelectedDate}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '5px', display: 'block', color: '#495057' }}>Início:</label>
                  <input
                    type="text"
                    value={startTimeInput}
                    onChange={(e) => handleTimeChange(e.target.value, 'start')}
                    placeholder="HH:MM"
                    className="form-control"
                    style={{
                      border: `1px solid ${timeErrors.start ? '#dc3545' : '#ddd'}`,
                      textAlign: 'center'
                    }}
                  />
                  {timeErrors.start && (
                    <small style={{ color: '#dc3545', fontSize: '11px' }}>{timeErrors.start}</small>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '5px', display: 'block', color: '#495057' }}>Fim:</label>
                  <input
                    type="text"
                    value={endTimeInput}
                    onChange={(e) => handleTimeChange(e.target.value, 'end')}
                    placeholder="HH:MM"
                    className="form-control"
                    style={{
                      border: `1px solid ${timeErrors.end ? '#dc3545' : '#ddd'}`,
                      textAlign: 'center'
                    }}
                  />
                  {timeErrors.end && (
                    <small style={{ color: '#dc3545', fontSize: '11px' }}>{timeErrors.end}</small>
                  )}
                </div>
              </div>
              
              {/* Botão Salvar */}
              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={handleSave}
                  disabled={loading || !selectedDate || !startTime || !endTime}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: '1px solid #28a745',
                    borderRadius: '4px',
                    cursor: loading || !selectedDate || !startTime || !endTime ? 'not-allowed' : 'pointer',
                    opacity: loading || !selectedDate || !startTime || !endTime ? 0.6 : 1,
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {loading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>

            {/* Legenda */}
            <div style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
              <h6 style={{ margin: '0 0 8px 0', fontSize: '12px' }}>Legenda:</h6>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '12px', height: '12px', backgroundColor: '#e3f2fd', border: '2px dashed #2196f3', borderRadius: '2px' }}></div>
                  <span>Nova Banca (Preview)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '12px', height: '12px', backgroundColor: '#d4edda', border: '1px solid #28a745', borderRadius: '2px' }}></div>
                  <span>Banca - Meu Processo</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '12px', height: '12px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '2px' }}></div>
                  <span>Banca - Outros</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '12px', height: '12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '2px' }}></div>
                  <span>Outras Atividades</span>
                </div>
              </div>
            </div>

            {/* Atividades do dia */}
            <div style={{ flex: 1, overflow: 'auto', padding: '10px' }}>
              <h6 style={{ margin: '0 0 8px 0', fontSize: '12px' }}>
                Atividades {selectedDate && `- ${format(selectedDate, 'dd/MM', { locale: ptBR })}`}:
              </h6>
              {atividades.length === 0 ? (
                <p style={{ fontSize: '11px', color: '#6c757d', margin: 0 }}>
                  {selectedDate ? 'Nenhuma atividade' : 'Selecione uma data'}
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {atividades.map((atividade, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '6px',
                        backgroundColor: atividade.isCurrentProcess ? '#d4edda' : 'white',
                        border: `1px solid ${atividade.isCurrentProcess ? '#28a745' : '#dee2e6'}`,
                        borderRadius: '3px',
                        cursor: atividade.isCurrentProcess ? 'pointer' : 'default',
                        fontSize: '10px'
                      }}
                      onClick={() => {
                        if (atividade.isCurrentProcess && atividade.isBanca) {
                          const [hours, minutes] = atividade.hora.split(':');
                          const bancaDate = new Date(selectedDate || new Date());
                          bancaDate.setHours(parseInt(hours), parseInt(minutes));
                          
                          const endDate = new Date(bancaDate.getTime() + 60 * 60 * 1000);
                          
                          setStartTime(bancaDate);
                          setEndTime(endDate);
                          setStartTimeInput(format(bancaDate, 'HH:mm'));
                          setEndTimeInput(format(endDate, 'HH:mm'));
                          
                          toast.success(`Horário assumido: ${atividade.hora}`);
                        }
                      }}
                    >
                      <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                        {atividade.hora} - {atividade.processo.nome}
                      </div>
                      <div>{BancaTagUtils.removeBancaTag(atividade.descricao)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Botão Fechar */}
            <div style={{ padding: '15px', borderTop: '1px solid #eee', backgroundColor: 'white' }}>
              <button
                onClick={onClose}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #6c757d',
                  background: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#6c757d'
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CronogramaModal;